import type { Lead } from '../types/lead';
import type { Place } from '../types/place';

export const DEMO_VISIBLE_ITEMS = 5;

type RandomSource = () => number;
type MutableRecord = Record<string, unknown>;
type DemoVisibilityRecord = { _demoVisible?: boolean; deface?: boolean };

const STRUCTURAL_CODES = new Set([32, 40, 41, 42, 43, 46, 47, 58, 61, 63, 64, 95]);

function cloneRecord<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function randomAsciiLetter(random: RandomSource): string {
  return String.fromCharCode(Math.floor(random() * 26) + 97);
}

/**
 * Registros locais carregam a decisão estável feita sobre a lista inteira. Respostas legadas já
 * mascaradas preservam a decisão do backend. Um payload grande, limpo e sem marcador falha para
 * o lado conservador: nenhum item ganha visibilidade por causa da ordem corrente.
 */
function visibleRecordPredicate(records: readonly DemoVisibilityRecord[]) {
  const hasStableMarkers = records.some((record) => typeof record._demoVisible === 'boolean');
  const hasLegacyMasks = records.some((record) => record.deface === true);

  return (record: DemoVisibilityRecord): boolean => {
    if (hasStableMarkers) return record._demoVisible === true;
    if (hasLegacyMasks) return record.deface !== true;
    return records.length <= DEMO_VISIBLE_ITEMS;
  };
}

/**
 * Porte de `String.prototype.deface` [app.js:66]. A checagem explícita dos surrogates corrige
 * [app.js:75]: sem `u`, as faixas de emoji da regex são interpretadas como unidades UTF-16
 * quebradas (e ainda englobam caracteres ASCII que deveriam ser embaralhados).
 */
export function defaceString(value: unknown, random: RandomSource = Math.random): string {
  const text = String(value ?? '');
  const output: string[] = [];

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index] ?? '';
    const code = char.charCodeAt(0);
    const isSurrogate = code >= 0xd800 && code <= 0xdfff;
    const isSymbol = code >= 0x203c && code <= 0x3299;

    if (isSurrogate || isSymbol || code === 0x200d || code === 0xfe0f) {
      output.push(char);
    } else if (code >= 48 && code <= 57) {
      output.push(String.fromCharCode(Math.floor(random() * 10) + 48));
    } else if (!STRUCTURAL_CODES.has(code)) {
      let generated = randomAsciiLetter(random);
      generated = char === char.toUpperCase() ? generated.toUpperCase() : generated.toLowerCase();
      output.push(generated);
    } else {
      output.push(char);
    }
  }

  return output.join('');
}

/** Porte de `defaceLeads` [app.js:2345]. Não altera os registros limpos recebidos. */
export function defaceLeads(
  leads: readonly Lead[],
  random: RandomSource = Math.random,
): Lead[] {
  const isVisible = visibleRecordPredicate(leads);
  return leads.map((lead) => {
    const visible = isVisible(lead);
    const masked = cloneRecord(lead);
    delete masked._demoVisible;
    delete masked.social_network;
    delete masked.username;

    if (!visible && lead.deface !== true) {
      if (masked.email) masked.email = defaceString(masked.email, random);
      if (masked.phone) masked.phone = defaceString(masked.phone, random);
      if (masked.url) masked.url = defaceString(masked.url, random);
      if (masked.headline) masked.headline = defaceString(masked.headline, random);
      if (masked.company) masked.company = defaceString(masked.company, random);
      if (masked.location) masked.location = defaceString(masked.location, random);
    }
    if (!visible || lead.deface === true) masked.deface = true;

    return masked;
  });
}

/** Porte de `defacePlaces` [app.js:2381]. Mantém inclusive os shapes incomuns do legado. */
export function defacePlaces(
  places: readonly Place[],
  random: RandomSource = Math.random,
): Place[] {
  const isVisible = visibleRecordPredicate(places);
  return places.map((place) => {
    const visible = isVisible(place);
    const masked = cloneRecord(place) as unknown as MutableRecord;
    delete masked['_demoVisible'];
    if (visible) return masked as unknown as Place;
    if (place.deface === true) return masked as unknown as Place;

    const categories = masked['categories'];
    if (Array.isArray(categories)) {
      masked['categories'] = [defaceString(categories.join(','), random)];
    }

    const websites = masked['websites'];
    if (Array.isArray(websites)) {
      masked['websites'] = [defaceString(websites.join(','), random)];
    }

    for (const field of ['image', 'cid', 'placeID']) {
      if (masked[field]) masked[field] = defaceString(masked[field], random);
    }

    const location = masked['location'];
    if (Array.isArray(location)) {
      masked['location'] = [defaceString(location.join(','), random)];
    }

    const address = masked['address'];
    if (Array.isArray(address)) {
      masked['address'] = address.map((row) => defaceString(row, random));
    }

    const phones = masked['phones'];
    if (Array.isArray(phones)) {
      masked['phones'] = [defaceString(phones.join(','), random)];
    }

    const reviews = masked['reviews'];
    if (reviews && typeof reviews === 'object' && !Array.isArray(reviews)) {
      const mutableReviews = reviews as MutableRecord;
      mutableReviews['count'] = '?.?';
      mutableReviews['avg'] = Math.floor(random() * 9999) + 1;
    }

    masked['deface'] = true;
    return masked as unknown as Place;
  });
}
